import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, where, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Icons } from '../components/Icons';

function ConsignorSettlement() {
  const { user } = useAuth();
  const [consignors, setConsignors] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsignor, setSelectedConsignor] = useState(null);
  const [settlementPeriod, setSettlementPeriod] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementData, setSettlementData] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        loadConsignors(),
        loadSettlements()
      ]);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConsignors = async () => {
    try {
      // 위탁자 목록 수집 (sales에서 unique consignorName 추출)
      const salesRef = collection(db, 'sales');
      const snapshot = await getDocs(salesRef);

      const consignorMap = {};

      snapshot.docs.forEach(doc => {
        const sale = doc.data();
        if (sale.consignorName) {
          if (!consignorMap[sale.consignorName]) {
            consignorMap[sale.consignorName] = {
              name: sale.consignorName,
              totalSales: 0,
              totalRevenue: 0,
              totalCommission: 0,
              salesCount: 0
            };
          }

          consignorMap[sale.consignorName].salesCount++;
          consignorMap[sale.consignorName].totalRevenue += sale.actualSalePrice || 0;
          consignorMap[sale.consignorName].totalCommission += sale.commissionAmount || 0;
        }
      });

      setConsignors(Object.values(consignorMap));
    } catch (error) {
      console.error('위탁자 로드 오류:', error);
    }
  };

  const loadSettlements = async () => {
    try {
      const settlementsRef = collection(db, 'settlements');
      const q = query(settlementsRef, orderBy('settlementDate', 'desc'));
      const snapshot = await getDocs(q);

      const settlementList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        settlementDate: doc.data().settlementDate?.toDate?.() || new Date(),
        periodStart: doc.data().periodStart?.toDate?.() || new Date(),
        periodEnd: doc.data().periodEnd?.toDate?.() || new Date(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setSettlements(settlementList);
    } catch (error) {
      console.error('정산 내역 로드 오류:', error);
    }
  };

  const calculateSettlement = async () => {
    if (!selectedConsignor) {
      alert('위탁자를 선택해주세요.');
      return;
    }

    try {
      const startDate = new Date(settlementPeriod.startDate);
      const endDate = new Date(settlementPeriod.endDate);
      endDate.setHours(23, 59, 59);

      // 해당 기간의 판매 내역 조회
      const salesRef = collection(db, 'sales');
      const q = query(
        salesRef,
        where('consignorName', '==', selectedConsignor.name)
      );
      const snapshot = await getDocs(q);

      const salesInPeriod = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          saleDate: doc.data().saleDate?.toDate?.() || new Date()
        }))
        .filter(sale => sale.saleDate >= startDate && sale.saleDate <= endDate);

      if (salesInPeriod.length === 0) {
        alert('해당 기간에 판매 내역이 없습니다.');
        return;
      }

      const totalRevenue = salesInPeriod.reduce((sum, sale) => sum + (sale.actualSalePrice || 0), 0);
      const totalCommission = salesInPeriod.reduce((sum, sale) => sum + (sale.commissionAmount || 0), 0);
      const netAmount = totalRevenue - totalCommission;

      setSettlementData({
        consignorName: selectedConsignor.name,
        periodStart: startDate,
        periodEnd: endDate,
        salesCount: salesInPeriod.length,
        totalRevenue,
        totalCommission,
        netAmount,
        sales: salesInPeriod
      });

      setShowSettlementModal(true);
    } catch (error) {
      console.error('정산 계산 오류:', error);
      alert('정산 계산 중 오류가 발생했습니다.');
    }
  };

  const confirmSettlement = async () => {
    if (!settlementData) return;

    try {
      // 정산 기록 저장
      await addDoc(collection(db, 'settlements'), {
        consignorName: settlementData.consignorName,
        periodStart: settlementData.periodStart,
        periodEnd: settlementData.periodEnd,
        salesCount: settlementData.salesCount,
        totalRevenue: settlementData.totalRevenue,
        totalCommission: settlementData.totalCommission,
        netAmount: settlementData.netAmount,
        settlementDate: new Date(),
        createdAt: serverTimestamp(),
        createdBy: user.email,
        status: 'completed'
      });

      alert('정산이 완료되었습니다.');
      setShowSettlementModal(false);
      setSettlementData(null);
      loadData();
    } catch (error) {
      console.error('정산 저장 오류:', error);
      alert('정산 저장 중 오류가 발생했습니다.');
    }
  };

  const exportSettlementToExcel = (settlement) => {
    const data = settlement.sales.map(sale => ({
      '판매일': sale.saleDate.toLocaleDateString('ko-KR'),
      '제품명': sale.productName,
      '판매가': sale.actualSalePrice,
      '수수료율': `${sale.consignorCommission}%`,
      '수수료금액': sale.commissionAmount,
      '정산금액': sale.actualSalePrice - sale.commissionAmount
    }));

    // 요약 추가
    data.push({
      '판매일': '',
      '제품명': '총계',
      '판매가': settlement.totalRevenue,
      '수수료율': '',
      '수수료금액': settlement.totalCommission,
      '정산금액': settlement.netAmount
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '정산내역');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${settlement.consignorName}_정산내역_${settlement.periodStart.toISOString().split('T')[0]}_${settlement.periodEnd.toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">위탁자 정산</h2>
          <p className="text-gray-500 mt-1">위탁 판매 수익을 정산하고 관리합니다.</p>
        </div>
      </div>

      {/* 정산 계산 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-8">
        <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
          <Icons.Calculator className="w-6 h-6 mr-2 text-brand-500" />
          새 정산 계산
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-bold text-brand-900 mb-2">위탁자 선택</label>
            <div className="relative">
              <select
                value={selectedConsignor?.name || ''}
                onChange={(e) => setSelectedConsignor(consignors.find(c => c.name === e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 appearance-none transition-colors"
              >
                <option value="">선택하세요</option>
                {consignors.map(consignor => (
                  <option key={consignor.name} value={consignor.name}>
                    {consignor.name} ({consignor.salesCount}건)
                  </option>
                ))}
              </select>
              <Icons.ChevronDown className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-900 mb-2">시작일</label>
            <input
              type="date"
              value={settlementPeriod.startDate}
              onChange={(e) => setSettlementPeriod({ ...settlementPeriod, startDate: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-900 mb-2">종료일</label>
            <input
              type="date"
              value={settlementPeriod.endDate}
              onChange={(e) => setSettlementPeriod({ ...settlementPeriod, endDate: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            />
          </div>
        </div>

        <button
          onClick={calculateSettlement}
          className="w-full px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold flex items-center justify-center"
        >
          <Icons.Calculator className="w-5 h-5 mr-2" />
          정산 계산하기
        </button>
      </div>

      {/* 위탁자 목록 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-8">
        <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
          <Icons.Users className="w-6 h-6 mr-2 text-brand-500" />
          위탁자 현황
        </h3>

        {consignors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">위탁자</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">판매건수</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">총 매출</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">총 수수료</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">정산금액</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {consignors.map(consignor => (
                  <tr key={consignor.name} className="hover:bg-brand-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-bold text-brand-900">{consignor.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">{consignor.salesCount}건</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">{consignor.totalRevenue.toLocaleString()}원</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-red-600">{consignor.totalCommission.toLocaleString()}원</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-green-600">
                      {(consignor.totalRevenue - consignor.totalCommission).toLocaleString()}원
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Icons.Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">위탁 판매 내역이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 정산 내역 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
        <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
          <Icons.FileText className="w-6 h-6 mr-2 text-brand-500" />
          정산 내역
        </h3>

        {settlements.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
            {settlements.map(settlement => (
              <div key={settlement.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-300 hover:border-brand-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="text-lg font-bold text-brand-900">{settlement.consignorName}</h4>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                        정산완료
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Icons.Calendar className="w-4 h-4 mr-1" />
                      {settlement.periodStart.toLocaleDateString('ko-KR')} ~ {settlement.periodEnd.toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 mb-1">정산일</div>
                    <div className="font-medium text-gray-900">{settlement.settlementDate.toLocaleDateString('ko-KR')}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">판매건수</div>
                    <div className="text-lg font-bold text-gray-900">{settlement.salesCount}건</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">총 매출</div>
                    <div className="text-lg font-bold text-gray-900">{settlement.totalRevenue.toLocaleString()}원</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">수수료</div>
                    <div className="text-lg font-bold text-red-600">{settlement.totalCommission.toLocaleString()}원</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">정산금액</div>
                    <div className="text-lg font-bold text-green-600">{settlement.netAmount.toLocaleString()}원</div>
                  </div>
                </div>

                <button
                  onClick={() => exportSettlementToExcel(settlement)}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition font-medium flex items-center justify-center shadow-sm"
                >
                  <Icons.Download className="w-4 h-4 mr-2" />
                  엑셀 다운로드
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Icons.FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">정산 내역이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 정산 확인 모달 */}
      {showSettlementModal && settlementData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all scale-100">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
                <h2 className="text-2xl font-bold text-brand-900 flex items-center">
                  <Icons.Check className="w-6 h-6 mr-2 text-brand-500" />
                  정산 확인
                </h2>
                <button
                  onClick={() => setShowSettlementModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <Icons.X className="w-6 h-6" />
                </button>
              </div>

              {/* 정산 요약 */}
              <div className="bg-brand-50 rounded-xl p-6 mb-8 border border-brand-100">
                <h3 className="font-bold text-lg text-brand-900 mb-4 flex items-center">
                  <Icons.FileText className="w-5 h-5 mr-2 text-brand-600" />
                  정산 요약
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <div className="text-sm text-brand-600 mb-1">위탁자</div>
                    <div className="text-xl font-bold text-brand-900">{settlementData.consignorName}</div>
                  </div>
                  <div>
                    <div className="text-sm text-brand-600 mb-1">정산 기간</div>
                    <div className="font-medium text-brand-900">
                      {settlementData.periodStart.toLocaleDateString('ko-KR')} ~ {settlementData.periodEnd.toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-brand-600 mb-1">판매 건수</div>
                    <div className="text-xl font-bold text-brand-900">{settlementData.salesCount}건</div>
                  </div>
                  <div>
                    <div className="text-sm text-brand-600 mb-1">총 매출</div>
                    <div className="text-xl font-bold text-brand-900">{settlementData.totalRevenue.toLocaleString()}원</div>
                  </div>
                  <div>
                    <div className="text-sm text-brand-600 mb-1">총 수수료</div>
                    <div className="text-xl font-bold text-red-600">-{settlementData.totalCommission.toLocaleString()}원</div>
                  </div>
                  <div>
                    <div className="text-sm text-brand-600 mb-1">정산 금액</div>
                    <div className="text-2xl font-bold text-green-600">{settlementData.netAmount.toLocaleString()}원</div>
                  </div>
                </div>
              </div>

              {/* 판매 상세 내역 */}
              <div className="mb-8">
                <h3 className="font-bold text-lg text-brand-900 mb-4 flex items-center">
                  <Icons.List className="w-5 h-5 mr-2 text-brand-600" />
                  판매 상세 내역
                </h3>
                <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl shadow-inner">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">판매일</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">제품명</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">판매가</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">수수료</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">정산액</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {settlementData.sales.map((sale, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-3 text-sm text-gray-600">{sale.saleDate.toLocaleDateString('ko-KR')}</td>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">{sale.productName}</td>
                          <td className="px-6 py-3 text-sm text-right text-gray-600">{sale.actualSalePrice.toLocaleString()}원</td>
                          <td className="px-6 py-3 text-sm text-right text-red-600">
                            {sale.commissionAmount.toLocaleString()}원 <span className="text-xs text-gray-400">({sale.consignorCommission}%)</span>
                          </td>
                          <td className="px-6 py-3 text-sm text-right font-bold text-green-600">
                            {(sale.actualSalePrice - sale.commissionAmount).toLocaleString()}원
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex space-x-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowSettlementModal(false)}
                  className="flex-1 px-6 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition font-medium"
                >
                  취소
                </button>
                <button
                  onClick={() => exportSettlementToExcel(settlementData)}
                  className="flex-1 px-6 py-3 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-lg transition font-bold flex items-center justify-center"
                >
                  <Icons.Download className="w-5 h-5 mr-2" />
                  엑셀 다운로드
                </button>
                <button
                  onClick={confirmSettlement}
                  className="flex-1 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold flex items-center justify-center"
                >
                  <Icons.Check className="w-5 h-5 mr-2" />
                  정산 완료
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsignorSettlement;
